import { WhatsApp } from 'constants/messageMediums';
import { selectDoesUserHaveRbacPermission, selectNextBestActionEnabled } from 'core/userContext';
import { NBACampaignsButton } from 'modules/ai/nba/components/campaigns/NBACampaignsButton';
import { NBACampaignsSlideover } from 'modules/ai/nba/components/campaigns/NBACampaignsSlideOver';
import { useEspAccounts } from 'modules/channels/hooks/useESPAccounts';
import { FolderId } from 'modules/domains/foldering';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Analytics24 } from 'ui/icons';
import { LinkAddButton } from 'ui/linkButton';
import { PageHeader } from 'ui/pageHeader';
import { spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import {
  useReactCampaignV1Feature,
  useWhatsAppMessageChannelFeature,
} from 'utils/hooks/featureFlags';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { CreateCampaignMenu } from './components/CreateCampaignMenu';
import { useCampaignsTranslation } from './hooks';
import { useQueryHasApprovedBusinessTemplates } from './hooks/useQueryHasApprovedBusinessTemplates';

// TODO: [PRO-3634] `displayContent` convention for localization. Turn text to translate into a localization en-US.json
export const displayContent = {
  labelNewCampaign: 'New campaign',
  labelViewInsights: 'View insights',
  disabledNewCampaignTooltip:
    'You do not have permission to create new campaigns. Please contact your admin to update your permissions.',
};

const StyledButtons = styled.div`
  display: flex;
  > * {
    margin-left: ${spacing.half};
    &:first-child {
      margin-left: 0;
    }
  }
`;

const StyledLink = styled(Link)`
  &:hover {
    text-decoration: none;
  }
`;

type Props = {
  content?: {
    labelNewCampaign: string;
    labelViewInsights: string;
    disabledNewCampaignTooltip: string;
  };
  currentFolderId?: FolderId | null;
};

export const CampaignIndexHeader = ({
  content = displayContent,
  currentFolderId = null,
}: Props) => {
  const { espAccounts, isLoadingEspAccounts } = useEspAccounts(WhatsApp);
  const hasWhatsAppAccounts = !!(espAccounts?.length > 0);

  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const isReactCampaignV1FeatureOn = useReactCampaignV1Feature();
  const isNBAEnabled = useSelector(selectNextBestActionEnabled);

  const { t } = useCampaignsTranslation();
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );
  const [isNBACampaignsSlideOverOpen, setIsNBACampaignsSlideOverOpen] = useState(false);

  const { hasApprovedBusinessTemplates } = useQueryHasApprovedBusinessTemplates({
    skip: !isWhatsAppFFOn,
  });

  return (
    <PageHeader
      data-test="campaign-index-page-header"
      pageActions={
        <StyledButtons>
          <StyledLink to="/messagingInsights">
            <SecondaryButton data-test="campaign-view-insights-button" size={BUTTON_SIZES.LARGE}>
              <Analytics24 data-test="campaign-view-insights-icon" />
              <span data-test="campaign-view-insights-label">{content.labelViewInsights}</span>
            </SecondaryButton>
          </StyledLink>
          <Tooltip
            isEnabled={!canDraftMessaging}
            tooltipContent={content.disabledNewCampaignTooltip}
          >
            {isReactCampaignV1FeatureOn ? (
              <CreateCampaignMenu
                canDraftMessaging={canDraftMessaging}
                hasWhatsAppAccounts={hasWhatsAppAccounts}
                isLoadingEspAccounts={isLoadingEspAccounts}
                hasApprovedBusinessTemplates={hasApprovedBusinessTemplates}
              />
            ) : (
              <LinkAddButton
                to={`/campaigns/create?destinationFolderId=${currentFolderId}`}
                size={BUTTON_SIZES.LARGE}
                isDisabled={!canDraftMessaging}
                isAngular
              >
                <span>{content.labelNewCampaign}</span>
              </LinkAddButton>
            )}
          </Tooltip>
        </StyledButtons>
      }
    >
      {t('title')}
      {isNBAEnabled && <NBACampaignsButton onClick={() => setIsNBACampaignsSlideOverOpen(true)} />}
      {isNBACampaignsSlideOverOpen && (
        <NBACampaignsSlideover
          onClose={() => {
            setIsNBACampaignsSlideOverOpen(false);
          }}
          onClickOutside={() => {
            setIsNBACampaignsSlideOverOpen(false);
          }}
        />
      )}
    </PageHeader>
  );
};
